import { createContext, useContext, useReducer } from "react";

/**
 * Toast alerts
 *
 * Example usage:
 *
 * const { notify, dismiss, instances } = useToast();
 * notify("A toast", ToastType.Error)
 * dismiss(instances[0].id)
 */

export enum ToastType {
  Info,
  Warning,
  Error,
}

type Toast = {
  id: string;
  message: string | JSX.Element;
  type: ToastType;
};

type ToastContextProps = {
  instances: Toast[];
  notify: (message: string | JSX.Element, type: ToastType) => string;
  dismiss: (id: string) => void;
};

export const ToastContext = createContext<ToastContextProps>({
  dismiss: () => {},
  instances: [],
  notify: () => "",
});

ToastContext.displayName = "ToastContext"; // used by react devtools

enum ToastAction {
  Show,
  Dismiss,
}

type ToastReducerActionShow = {
  type: ToastAction.Show;
  value: Toast;
};

type ToastReducerActionDismiss = {
  type: ToastAction.Dismiss;
  value: string;
};

type ToastReducerAction = ToastReducerActionShow | ToastReducerActionDismiss;

const toastReducer = (state: Toast[], action: ToastReducerAction) => {
  switch (action.type) {
    case ToastAction.Show: {
      return [...state, action.value];
    }
    case ToastAction.Dismiss: {
      return state.filter((item) => item.id !== action.value);
    }
    default:
      return state;
  }
};

// @ts-ignore (fix me please, do not replicate)
export const ToastProvider = ({ children }) => {
  const [instances, dispatch] = useReducer(toastReducer, []);

  return (
    <ToastContext.Provider
      value={{
        dismiss: (id: string) => {
          dispatch({ type: ToastAction.Dismiss, value: id });
        },
        instances,
        notify: (message, type) => {
          const randomId = Math.random().toString(36).substring(7);

          dispatch({
            type: ToastAction.Show,
            value: { id: randomId, message, type },
          });

          return randomId;
        },
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
